import categoryService from "../../../services/ticketService/categoryService";
import { CategoryInterface } from "../../../models/TicketModule/Category";
import { CACHE_KEY_CATEGORIES } from "../../../utils/constants";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface UpdateCategoriesContext {
  previousCategories: CategoryInterface[];
}

const useUpdateCategory = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    CategoryInterface,
    Error,
    CategoryInterface,
    UpdateCategoriesContext
  >({
    mutationFn: async (category: CategoryInterface) => {
      // Assuming the ID is stored in the category object or can be derived
      const { id, ...categoryData } = category;
      return categoryService.Update(id || "", categoryData);
    },

    onMutate: async (updatedCategory: CategoryInterface) => {
      await queryClient.cancelQueries({ queryKey: [CACHE_KEY_CATEGORIES] });

      const previousCategories =
        queryClient.getQueryData<CategoryInterface[]>([
          CACHE_KEY_CATEGORIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      queryClient.setQueryData<CategoryInterface[]>(
        [
          CACHE_KEY_CATEGORIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (categories = []) =>
          categories.map((category) =>
            category.id === updatedCategory.id ? updatedCategory : category
          )
      );

      return { previousCategories };
    },

    onSuccess: (savedCategory) => {
      queryClient.setQueryData<CategoryInterface[]>(
        [
          CACHE_KEY_CATEGORIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (categories) =>
          categories?.map((category) =>
            category.id === savedCategory.id ? savedCategory : category
          )
      );
    },

    onError: (error, updatedCategory, context) => {
      if (!context) return;

      queryClient.setQueryData<CategoryInterface[]>(
        [
          CACHE_KEY_CATEGORIES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousCategories
      );
    },
  });
};

export default useUpdateCategory;
